// src/components/admin/contas/SubContasVip.tsx
// GRAIA Admin — Contas VIP: grid de cards dourados + modal Criar/Promover + modal Gerenciar
import React, { useEffect, useState, useCallback, useImperativeHandle } from 'react';
import { Plus, X, Star, UserPlus, Search, Ban, CalendarDays, Infinity, Settings, CalendarPlus, CalendarMinus } from 'lucide-react';
import './SubContasVip.css';

interface VipUser {
    _id: string;
    username: string;
    email: string;
    vipAccess: {
        active: boolean;
        expiresAt: string | null;
        grantedAt: string | null;
    };
}

type ModalMode = 'create' | 'promote';

// ── Fix de timezone: interpreta a string YYYY-MM-DD como data local, não UTC ──
function parseDateLocal(dateStr: string): Date {
    const [year, month, day] = dateStr.split('-').map(Number);
    // new Date(year, month-1, day) usa o fuso local → sem off-by-one
    return new Date(year, month - 1, day, 23, 59, 59);
}

function fmtDate(d?: string | null) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('pt-BR');
}

function isExpired(d?: string | null) {
    if (!d) return false;
    return new Date(d) < new Date();
}

function initials(name: string) {
    return name.slice(0, 2).toUpperCase();
}

// Formata Date → string YYYY-MM-DD para input[type=date]
function toInputDate(d: Date): string {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

// ══════════════════════════════════════════════════════════════
const SubContasVip = React.forwardRef<{ openModal: (mode: ModalMode) => void }, {}>((_, ref) => {
    const token = localStorage.getItem('accessToken');
    const [users, setUsers] = useState<VipUser[]>([]);
    const [loading, setLoading] = useState(true);

    // Modal criar/promover
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState<ModalMode>('create');

    // Criar
    const [form, setForm] = useState({ username: '', email: '', password: '' });
    const [isLifetime, setIsLifetime] = useState(true);
    const [expiresAt, setExpiresAt] = useState('');
    const [saving, setSaving] = useState(false);
    const [createError, setCreateError] = useState('');

    // Promover
    const [promoteSearch, setPromoteSearch] = useState('');
    const [promoteResults, setPromoteResults] = useState<{ _id: string; username: string; email: string }[]>([]);
    const [selectedUser, setSelectedUser] = useState<{ _id: string; username: string } | null>(null);
    const [promoteLifetime, setPromoteLifetime] = useState(true);
    const [promoteExpiry, setPromoteExpiry] = useState('');
    const [promoting, setPromoting] = useState(false);
    const [promoteError, setPromoteError] = useState('');

    // Modal gerenciar VIP
    const [manageUser, setManageUser] = useState<VipUser | null>(null);
    const [manageTab, setManageTab] = useState<'extend' | 'shorten' | 'revoke'>('extend');
    const [manageDate, setManageDate] = useState('');
    const [manageLifetime, setManageLifetime] = useState(false);
    const [manageLoading, setManageLoading] = useState(false);
    const [manageError, setManageError] = useState('');
    const [manageSuccess, setManageSuccess] = useState('');

    // Confirmar revogação (dentro do modal gerenciar)
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmUserId, setConfirmUserId] = useState<string | null>(null);

    // ── Fetch VIPs ────────────────────────────────────────────
    const fetchVips = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/admin/accounts/vip', { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (data.success) setUsers(data.users);
        } finally { setLoading(false); }
    }, [token]);

    useEffect(() => { fetchVips(); }, [fetchVips]);

    // ── Abrir modal criar/promover ────────────────────────────
    const openModal = useCallback((mode: ModalMode) => {
        setModalMode(mode);
        setShowModal(true);
        setForm({ username: '', email: '', password: '' });
        setIsLifetime(true);
        setExpiresAt('');
        setCreateError('');
        setPromoteSearch('');
        setPromoteResults([]);
        setSelectedUser(null);
        setPromoteLifetime(true);
        setPromoteExpiry('');
        setPromoteError('');
    }, []);

    useImperativeHandle(ref, () => ({ openModal }), [openModal]);

    // ── Abrir modal gerenciar ─────────────────────────────────
    const openManage = (user: VipUser) => {
        setManageUser(user);
        setManageTab('extend');
        setManageError('');
        setManageSuccess('');
        setManageLifetime(false);

        // Pré-preenche com a data atual de expiração (+30 dias para estender)
        if (user.vipAccess.expiresAt) {
            const current = new Date(user.vipAccess.expiresAt);
            current.setDate(current.getDate() + 30);
            setManageDate(toInputDate(current));
        } else {
            // Era vitalício, sugere 1 ano a partir de hoje para encurtar
            const d = new Date();
            d.setFullYear(d.getFullYear() + 1);
            setManageDate(toInputDate(d));
        }
    };

    // ── Criar conta VIP ───────────────────────────────────────
    const handleCreate = async () => {
        if (!form.username || !form.email || !form.password) {
            setCreateError('Preencha todos os campos obrigatórios.');
            return;
        }
        if (!isLifetime && !expiresAt) {
            setCreateError('Selecione uma data de expiração ou marque como Vitalício.');
            return;
        }
        setSaving(true);
        setCreateError('');
        try {
            // Converte usando parseDateLocal para evitar off-by-one
            const expiresAtValue = isLifetime ? null : parseDateLocal(expiresAt).toISOString();
            const res = await fetch('/api/admin/accounts/create-vip', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...form, expiresAt: expiresAtValue })
            });
            const data = await res.json();
            if (data.success) { setShowModal(false); fetchVips(); }
            else setCreateError(data.message || 'Erro ao criar conta.');
        } finally { setSaving(false); }
    };

    // ── Buscar usuários ───────────────────────────────────────
    const searchUsers = useCallback(async (q: string) => {
        if (!q || q.length < 2) { setPromoteResults([]); return; }
        try {
            const res = await fetch(`/api/admin/subscriptions/users?search=${encodeURIComponent(q)}&limit=5`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) setPromoteResults(data.users);
        } catch { }
    }, [token]);

    // ── Promover existente ────────────────────────────────────
    const handlePromote = async () => {
        if (!selectedUser) return;
        if (!promoteLifetime && !promoteExpiry) {
            setPromoteError('Selecione uma data ou marque como Vitalício.');
            return;
        }
        setPromoting(true);
        setPromoteError('');
        try {
            // Converte usando parseDateLocal para evitar off-by-one
            const expiresAtValue = promoteLifetime ? null : parseDateLocal(promoteExpiry).toISOString();
            const res = await fetch(`/api/admin/accounts/${selectedUser._id}/grant-vip`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ expiresAt: expiresAtValue })
            });
            const data = await res.json();
            if (data.success) { setShowModal(false); fetchVips(); }
            else setPromoteError(data.message || 'Erro ao conceder VIP.');
        } finally { setPromoting(false); }
    };

    // ── Atualizar data de expiração do VIP (estender ou encurtar) ──
    const handleUpdateExpiry = async () => {
        if (!manageUser) return;
        if (!manageLifetime && !manageDate) {
            setManageError('Selecione uma data ou marque como Vitalício.');
            return;
        }
        setManageLoading(true);
        setManageError('');
        setManageSuccess('');
        try {
            const expiresAtValue = manageLifetime ? null : parseDateLocal(manageDate).toISOString();
            const res = await fetch(`/api/admin/accounts/${manageUser._id}/grant-vip`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ expiresAt: expiresAtValue })
            });
            const data = await res.json();
            if (data.success) {
                setManageSuccess(data.message || 'VIP atualizado com sucesso!');
                fetchVips();
                // Atualiza o manageUser localmente para refletir a mudança
                setManageUser(prev => prev ? {
                    ...prev,
                    vipAccess: {
                        ...prev.vipAccess,
                        expiresAt: expiresAtValue
                    }
                } : null);
            } else {
                setManageError(data.message || 'Erro ao atualizar VIP.');
            }
        } finally { setManageLoading(false); }
    };

    // ── Revogar ───────────────────────────────────────────────
    const confirmRevoke = async () => {
        if (!confirmUserId) return;
        setConfirmOpen(false);
        await fetch(`/api/admin/accounts/${confirmUserId}/revoke-vip`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` }
        });
        setConfirmUserId(null);
        setManageUser(null); // fecha modal de gerenciar se estiver aberto
        fetchVips();
    };

    // ── Adicionar dias/meses rapidamente ─────────────────────
    const addTime = (unit: 'days' | 'months', amount: number) => {
        const base = manageDate ? parseDateLocal(manageDate) : new Date();
        if (unit === 'days') base.setDate(base.getDate() + amount);
        else base.setMonth(base.getMonth() + amount);
        setManageDate(toInputDate(base));
        setManageLifetime(false);
    };

    const subtractTime = (unit: 'days' | 'months', amount: number) => {
        const base = manageDate ? parseDateLocal(manageDate) : new Date();
        if (unit === 'days') base.setDate(base.getDate() - amount);
        else base.setMonth(base.getMonth() - amount);
        // Não deixa data no passado
        if (base < new Date()) return;
        setManageDate(toInputDate(base));
        setManageLifetime(false);
    };

    // ── Input style reutilizável ──────────────────────────────
    const inputStyle = (disabled = false): React.CSSProperties => ({
        padding: '8px 10px', borderRadius: 7, border: '1px solid var(--sub-border)',
        background: 'var(--sub-surface-2)', color: 'var(--sub-text-primary)',
        fontSize: '0.84rem', outline: 'none', width: '100%', boxSizing: 'border-box',
        opacity: disabled ? 0.4 : 1,
    });

    // ═════════════════════════════════════════════════════════
    return (
        <div>
            {/* ── Grid de cards ────────────────────────────── */}
            {loading ? (
                <div className="vip-loading">
                    <div className="vip-spinner" /> Carregando contas VIP...
                </div>
            ) : users.length === 0 ? (
                <div className="vip-empty">
                    <div className="vip-empty-icon">⭐</div>
                    Nenhuma conta VIP encontrada.
                </div>
            ) : (
                <div className="vip-grid">
                    {users.map(u => {
                        const expired = isExpired(u.vipAccess.expiresAt);
                        return (
                            <div key={u._id} className="vip-card">
                                <div className="vip-card-top">
                                    <div className="vip-avatar">{initials(u.username)}</div>
                                    <div className="vip-card-info">
                                        <div className="vip-card-name">{u.username}</div>
                                        <div className="vip-card-email">{u.email}</div>
                                    </div>
                                    <span className="vip-badge"><Star size={10} /> VIP</span>
                                </div>

                                <div className="vip-card-meta">
                                    <div className="vip-meta-row">
                                        <span className="vip-meta-label">
                                            <CalendarDays size={10} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                                            Concedido
                                        </span>
                                        <span className="vip-meta-value">{fmtDate(u.vipAccess.grantedAt)}</span>
                                    </div>
                                    <div className="vip-meta-row">
                                        <span className="vip-meta-label">Expira em</span>
                                        <span className={`vip-meta-value ${!u.vipAccess.expiresAt ? 'lifetime' : expired ? 'expired' : ''}`}>
                                            {!u.vipAccess.expiresAt
                                                ? '♾ Vitalício'
                                                : expired
                                                    ? `✗ ${fmtDate(u.vipAccess.expiresAt)}`
                                                    : fmtDate(u.vipAccess.expiresAt)}
                                        </span>
                                    </div>
                                </div>

                                {/* Botão Gerenciar VIP (substituiu Revogar) */}
                                <button className="vip-manage-btn" onClick={() => openManage(u)}>
                                    <Settings size={13} /> Gerenciar VIP
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ══════════════════════════════════════════════
                MODAL CRIAR / PROMOVER
            ══════════════════════════════════════════════ */}
            {showModal && (
                <div className="vip-overlay" onClick={() => setShowModal(false)}>
                    <div className="vip-modal" onClick={e => e.stopPropagation()}>
                        <div className="vip-modal-header">
                            <h3 className="vip-modal-title">
                                <Star size={15} style={{ color: 'var(--vip-gold)' }} />
                                {modalMode === 'create' ? 'Nova Conta VIP' : 'Promover a VIP'}
                            </h3>
                            <button className="vip-modal-close" onClick={() => setShowModal(false)}>
                                <X size={15} />
                            </button>
                        </div>

                        <div className="vip-modal-body">
                            {/* Tabs */}
                            <div className="vip-mode-tabs">
                                <button className={`vip-mode-tab ${modalMode === 'create' ? 'active' : ''}`}
                                    onClick={() => { setModalMode('create'); setCreateError(''); setPromoteError(''); }}>
                                    <Plus size={13} /> Criar nova conta
                                </button>
                                <button className={`vip-mode-tab ${modalMode === 'promote' ? 'active' : ''}`}
                                    onClick={() => { setModalMode('promote'); setCreateError(''); setPromoteError(''); }}>
                                    <UserPlus size={13} /> Promover existente
                                </button>
                            </div>

                            {/* ── CRIAR ──────────────────────────────── */}
                            {modalMode === 'create' && (<>
                                <div className="vip-form-grid">
                                    <div className="vip-form-group">
                                        <label>Nome de usuário *</label>
                                        <input value={form.username}
                                            onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                                            placeholder="username" style={inputStyle()} />
                                    </div>
                                    <div className="vip-form-group">
                                        <label>Email *</label>
                                        <input type="email" value={form.email}
                                            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                                            placeholder="email@exemplo.com" style={inputStyle()} />
                                    </div>
                                    <div className="vip-form-group">
                                        <label>Senha *</label>
                                        <input type="password" value={form.password}
                                            onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                                            placeholder="Mínimo 6 caracteres" style={inputStyle()} />
                                    </div>
                                    <div className="vip-form-group">
                                        <label>Data de expiração</label>
                                        <input type="date" value={expiresAt}
                                            onChange={e => setExpiresAt(e.target.value)}
                                            disabled={isLifetime}
                                            style={inputStyle(isLifetime)} />
                                    </div>
                                </div>

                                <label className={`vip-lifetime-check ${isLifetime ? 'checked' : ''}`} style={{ marginBottom: 16 }}>
                                    <input type="checkbox" checked={isLifetime}
                                        onChange={e => { setIsLifetime(e.target.checked); if (e.target.checked) setExpiresAt(''); }} />
                                    <Infinity size={14} /> Acesso vitalício
                                </label>

                                {createError && (
                                    <p style={{ color: '#dc2626', fontSize: '0.82rem', marginBottom: 12, fontWeight: 600 }}>
                                        ✗ {createError}
                                    </p>
                                )}

                                <div className="vip-modal-footer">
                                    <button className="vip-btn secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                                    <button className="vip-btn primary" onClick={handleCreate}
                                        disabled={saving || !form.username || !form.email || !form.password}>
                                        {saving ? 'Criando...' : <><Star size={13} /> Criar Conta VIP</>}
                                    </button>
                                </div>
                            </>)}

                            {/* ── PROMOVER ───────────────────────────── */}
                            {modalMode === 'promote' && (<>
                                <div className="vip-form-group" style={{ marginBottom: 8 }}>
                                    <label>Buscar usuário</label>
                                    <div className="vip-search-wrap">
                                        <Search size={14} />
                                        <input
                                            style={{ ...inputStyle(), paddingLeft: 32 }}
                                            value={promoteSearch}
                                            onChange={e => { setPromoteSearch(e.target.value); searchUsers(e.target.value); }}
                                            placeholder="Digite nome ou email..." />
                                    </div>
                                </div>

                                {promoteResults.length > 0 && !selectedUser && (
                                    <div className="vip-search-results">
                                        {promoteResults.map(u => (
                                            <button key={u._id} className="vip-search-result"
                                                onClick={() => { setSelectedUser(u); setPromoteResults([]); setPromoteSearch(''); }}>
                                                <strong>{u.username}</strong>
                                                <span>{u.email}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {selectedUser && (
                                    <div className="vip-selected-chip">
                                        <Star size={13} style={{ color: 'var(--vip-gold)', flexShrink: 0 }} />
                                        Selecionado: <strong>{selectedUser.username}</strong>
                                        <button className="vip-selected-chip-clear"
                                            onClick={() => { setSelectedUser(null); setPromoteSearch(''); }}>
                                            <X size={13} />
                                        </button>
                                    </div>
                                )}

                                <div className="vip-form-group" style={{ marginBottom: 10 }}>
                                    <label>Data de expiração</label>
                                    <input type="date" value={promoteExpiry}
                                        onChange={e => setPromoteExpiry(e.target.value)}
                                        disabled={promoteLifetime}
                                        style={inputStyle(promoteLifetime)} />
                                </div>

                                <label className={`vip-lifetime-check ${promoteLifetime ? 'checked' : ''}`} style={{ marginBottom: 16 }}>
                                    <input type="checkbox" checked={promoteLifetime}
                                        onChange={e => { setPromoteLifetime(e.target.checked); if (e.target.checked) setPromoteExpiry(''); }} />
                                    <Infinity size={14} /> Acesso vitalício
                                </label>

                                {promoteError && (
                                    <p style={{ color: '#dc2626', fontSize: '0.82rem', marginBottom: 12, fontWeight: 600 }}>
                                        ✗ {promoteError}
                                    </p>
                                )}

                                <div className="vip-modal-footer">
                                    <button className="vip-btn secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                                    <button className="vip-btn primary" onClick={handlePromote}
                                        disabled={!selectedUser || promoting}>
                                        {promoting ? 'Concedendo...' : <><Star size={13} /> Conceder VIP</>}
                                    </button>
                                </div>
                            </>)}
                        </div>
                    </div>
                </div>
            )}

            {/* ══════════════════════════════════════════════
                MODAL GERENCIAR VIP
            ══════════════════════════════════════════════ */}
            {manageUser && (
                <div className="vip-overlay" onClick={() => { setManageUser(null); setConfirmOpen(false); setConfirmUserId(null); }}>
                    <div className="vip-modal vip-manage-modal" onClick={e => e.stopPropagation()}>
                        <div className="vip-modal-header">
                            <h3 className="vip-modal-title">
                                <Settings size={15} style={{ color: 'var(--vip-gold)' }} />
                                Gerenciar VIP — {manageUser.username}
                            </h3>
                            <button className="vip-modal-close" onClick={() => { setManageUser(null); setConfirmOpen(false); setConfirmUserId(null); }}>
                                <X size={15} />
                            </button>
                        </div>

                        <div className="vip-modal-body">
                            {/* Info atual */}
                            <div className="vip-manage-info-bar">
                                <div className="vip-manage-info-item">
                                    <span className="vip-manage-info-label">Usuário</span>
                                    <span className="vip-manage-info-value">{manageUser.username}</span>
                                </div>
                                <div className="vip-manage-info-item">
                                    <span className="vip-manage-info-label">Expiração atual</span>
                                    <span className={`vip-manage-info-value ${!manageUser.vipAccess.expiresAt ? 'lifetime' : isExpired(manageUser.vipAccess.expiresAt) ? 'expired' : 'active-date'}`}>
                                        {!manageUser.vipAccess.expiresAt
                                            ? '♾ Vitalício'
                                            : isExpired(manageUser.vipAccess.expiresAt)
                                                ? `✗ Expirado em ${fmtDate(manageUser.vipAccess.expiresAt)}`
                                                : fmtDate(manageUser.vipAccess.expiresAt)}
                                    </span>
                                </div>
                            </div>

                            {/* Tabs ação */}
                            <div className="vip-manage-tabs">
                                <button
                                    className={`vip-manage-tab extend ${manageTab === 'extend' ? 'active' : ''}`}
                                    onClick={() => { setManageTab('extend'); setManageError(''); setManageSuccess(''); }}>
                                    <CalendarPlus size={13} /> Estender
                                </button>
                                <button
                                    className={`vip-manage-tab shorten ${manageTab === 'shorten' ? 'active' : ''}`}
                                    onClick={() => { setManageTab('shorten'); setManageError(''); setManageSuccess(''); }}>
                                    <CalendarMinus size={13} /> Encurtar
                                </button>
                                <button
                                    className={`vip-manage-tab revoke ${manageTab === 'revoke' ? 'active' : ''}`}
                                    onClick={() => { setManageTab('revoke'); setManageError(''); setManageSuccess(''); }}>
                                    <Ban size={13} /> Revogar
                                </button>
                            </div>

                            {/* ── ESTENDER ─────────────────────────── */}
                            {manageTab === 'extend' && (
                                <div className="vip-manage-content">
                                    <p className="vip-manage-desc">
                                        Defina a nova data de expiração. Ela substituirá a data atual —
                                        certifique-se de que é posterior à data vigente.
                                    </p>

                                    {/* Atalhos rápidos */}
                                    <div className="vip-quick-actions">
                                        <span className="vip-quick-label">Adicionar a partir da data atual:</span>
                                        <div className="vip-quick-btns">
                                            {[
                                                { label: '+7d', unit: 'days' as const, amount: 7 },
                                                { label: '+1m', unit: 'months' as const, amount: 1 },
                                                { label: '+3m', unit: 'months' as const, amount: 3 },
                                                { label: '+6m', unit: 'months' as const, amount: 6 },
                                                { label: '+1a', unit: 'months' as const, amount: 12 },
                                            ].map(btn => (
                                                <button key={btn.label} className="vip-quick-btn extend"
                                                    onClick={() => addTime(btn.unit, btn.amount)}>
                                                    {btn.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="vip-form-group" style={{ marginBottom: 12 }}>
                                        <label>Nova data de expiração</label>
                                        <input type="date" value={manageDate}
                                            onChange={e => { setManageDate(e.target.value); setManageLifetime(false); }}
                                            disabled={manageLifetime}
                                            style={inputStyle(manageLifetime)} />
                                    </div>

                                    <label className={`vip-lifetime-check ${manageLifetime ? 'checked' : ''}`} style={{ marginBottom: 16 }}>
                                        <input type="checkbox" checked={manageLifetime}
                                            onChange={e => { setManageLifetime(e.target.checked); if (e.target.checked) setManageDate(''); }} />
                                        <Infinity size={14} /> Tornar vitalício
                                    </label>

                                    {manageError && <p className="vip-manage-error">✗ {manageError}</p>}
                                    {manageSuccess && <p className="vip-manage-success">✓ {manageSuccess}</p>}

                                    <div className="vip-modal-footer">
                                        <button className="vip-btn secondary" onClick={() => { setManageUser(null); setConfirmOpen(false); setConfirmUserId(null); }}>Cancelar</button>
                                        <button className="vip-btn primary" onClick={handleUpdateExpiry} disabled={manageLoading}>
                                            {manageLoading ? 'Salvando...' : <><CalendarPlus size={13} /> Confirmar Extensão</>}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* ── ENCURTAR ─────────────────────────── */}
                            {manageTab === 'shorten' && (
                                <div className="vip-manage-content">
                                    <p className="vip-manage-desc">
                                        Reduza o prazo do VIP. A nova data deve ser anterior à expiração atual
                                        e posterior à data de hoje.
                                    </p>

                                    {/* Atalhos de subtração */}
                                    <div className="vip-quick-actions">
                                        <span className="vip-quick-label">Remover da data atual:</span>
                                        <div className="vip-quick-btns">
                                            {[
                                                { label: '-7d', unit: 'days' as const, amount: 7 },
                                                { label: '-1m', unit: 'months' as const, amount: 1 },
                                                { label: '-3m', unit: 'months' as const, amount: 3 },
                                                { label: '-6m', unit: 'months' as const, amount: 6 },
                                            ].map(btn => (
                                                <button key={btn.label} className="vip-quick-btn shorten"
                                                    onClick={() => subtractTime(btn.unit, btn.amount)}>
                                                    {btn.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="vip-form-group" style={{ marginBottom: 16 }}>
                                        <label>Nova data de expiração (encurtada)</label>
                                        <input type="date" value={manageDate}
                                            onChange={e => { setManageDate(e.target.value); setManageLifetime(false); }}
                                            style={inputStyle()} />
                                    </div>

                                    {manageError && <p className="vip-manage-error">✗ {manageError}</p>}
                                    {manageSuccess && <p className="vip-manage-success">✓ {manageSuccess}</p>}

                                    <div className="vip-modal-footer">
                                        <button className="vip-btn secondary" onClick={() => { setManageUser(null); setConfirmOpen(false); setConfirmUserId(null); }}>Cancelar</button>
                                        <button className="vip-btn shorten-confirm" onClick={handleUpdateExpiry} disabled={manageLoading}>
                                            {manageLoading ? 'Salvando...' : <><CalendarMinus size={13} /> Confirmar Encurtamento</>}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* ── REVOGAR ──────────────────────────── */}
                            {manageTab === 'revoke' && (
                                <div className="vip-manage-content">
                                    <div className="vip-revoke-warning">
                                        <Ban size={24} className="vip-revoke-warning-icon" />
                                        <div>
                                            <p className="vip-revoke-warning-title">Atenção — esta ação é imediata</p>
                                            <p className="vip-revoke-warning-text">
                                                O acesso VIP de <strong>{manageUser.username}</strong> será revogado
                                                imediatamente e todas as compras ativas serão desativadas.
                                                O usuário perderá acesso na próxima requisição.
                                            </p>
                                        </div>
                                    </div>

                                    {/* Confirmação inline — sem modal extra */}
                                    {!confirmOpen ? (
                                        <div className="vip-modal-footer">
                                            <button className="vip-btn secondary" onClick={() => setManageUser(null)}>
                                                Cancelar
                                            </button>
                                            <button
                                                className="vip-btn revoke-confirm"
                                                onClick={() => {
                                                    setConfirmUserId(manageUser._id);
                                                    setConfirmOpen(true);
                                                }}
                                            >
                                                <Ban size={13} /> Revogar VIP Agora
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="vip-revoke-confirm-inline">
                                            <p className="vip-revoke-confirm-question">
                                                Tem certeza? Esta ação não pode ser desfeita.
                                            </p>
                                            <div className="vip-modal-footer">
                                                <button
                                                    className="vip-btn secondary"
                                                    onClick={() => {
                                                        setConfirmOpen(false);
                                                        setConfirmUserId(null);
                                                    }}
                                                >
                                                    Não, cancelar
                                                </button>
                                                <button
                                                    className="vip-btn revoke-confirm"
                                                    onClick={confirmRevoke}
                                                >
                                                    <Ban size={13} /> Sim, revogar
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

SubContasVip.displayName = 'SubContasVip';
export default SubContasVip;